import { useQuery } from "convex/react";
import { api } from "../../../convex/_generated/api";
import { FC } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Table,
  Flex,
  Heading,
  Text,
  Button,
  Card,
} from "@radix-ui/themes";
import { StatusBadge } from "./components/status-badge";
import { RelativeTime, formatDuration } from "./components/relative-time";
import { JsonInspector } from "./components/json-inspector";
import { Id } from "../../../convex/_generated/dataModel";

export const MembotRunDetailPage: FC = () => {
  const { runId } = useParams<{ runId: string }>();
  const navigate = useNavigate();

  const runDetail = useQuery(
    api.atlas.getRunDetail,
    runId ? { runId: runId as Id<"assistantRuns"> } : "skip"
  );

  // Get AI messages for the session if we have the run
  const aiMessages = useQuery(
    api.assistant.listAssistantAiMessages,
    runDetail?.sessionId ? { sessionId: runDetail.sessionId, limit: 200 } : "skip"
  );

  if (!runId) return <Text>Missing run ID</Text>;
  if (runDetail === undefined)
    return <Text color="gray">Loading run...</Text>;
  if (runDetail === null) return <Text color="red">Run not found.</Text>;

  // Filter messages for this run
  const runMessages = (aiMessages ?? []).filter(
    (msg) => msg.runId === runId
  );

  return (
    <div>
      <Flex direction="column" gap="1" mb="4">
        <Button
          variant="ghost"
          size="1"
          onClick={() => navigate(-1)}
          style={{ alignSelf: "flex-start" }}
        >
          &larr; Back
        </Button>
        <Flex align="center" gap="3">
          <Heading size="5">Run Detail</Heading>
          <StatusBadge status={runDetail.status} size="2" />
        </Flex>
      </Flex>

      {/* Run Info */}
      <Card mb="4">
        <Flex gap="4" wrap="wrap">
          <InfoItem label="Model" value={runDetail.model ?? "unknown"} />
          <InfoItem label="Workflow ID" value={runDetail.workflowId} mono />
          <InfoItem
            label="Duration"
            value={formatDuration(
              runDetail.startedAt,
              runDetail.completedAt ?? undefined
            )}
          />
          <InfoItem
            label="Signals"
            value={String(runDetail.signalIds.length)}
          />
          <InfoItem label="Session" value={runDetail.sessionId} mono />
        </Flex>
        {runDetail.error && (
          <Text color="red" size="2" mt="2" as="div">
            Error: {runDetail.error}
          </Text>
        )}
      </Card>

      {/* Signals */}
      <Heading size="3" mb="2">
        Signals ({runDetail.signals.length})
      </Heading>
      <Table.Root variant="surface" mb="4">
        <Table.Header>
          <Table.Row>
            <Table.ColumnHeaderCell>Source Kind</Table.ColumnHeaderCell>
            <Table.ColumnHeaderCell>Status</Table.ColumnHeaderCell>
            <Table.ColumnHeaderCell>Dedupe Key</Table.ColumnHeaderCell>
            <Table.ColumnHeaderCell>Payload</Table.ColumnHeaderCell>
            <Table.ColumnHeaderCell>Created</Table.ColumnHeaderCell>
          </Table.Row>
        </Table.Header>
        <Table.Body>
          {runDetail.signals.map((signal) =>
            signal ? (
              <Table.Row key={signal._id}>
                <Table.Cell>
                  <Text size="1" style={{ fontFamily: "monospace" }}>
                    {signal.sourceKind}
                  </Text>
                </Table.Cell>
                <Table.Cell>
                  <StatusBadge status={signal.status} />
                </Table.Cell>
                <Table.Cell>
                  <Text size="1" style={{ fontFamily: "monospace" }}>
                    {signal.dedupeKey.length > 30
                      ? signal.dedupeKey.slice(0, 30) + "..."
                      : signal.dedupeKey}
                  </Text>
                </Table.Cell>
                <Table.Cell>
                  {signal.payload ? (
                    <JsonInspector data={signal.payload} label="Payload" />
                  ) : (
                    "—"
                  )}
                </Table.Cell>
                <Table.Cell>
                  <RelativeTime timestamp={signal.createdAt} />
                </Table.Cell>
              </Table.Row>
            ) : null
          )}
          {runDetail.signals.length === 0 && (
            <Table.Row>
              <Table.Cell colSpan={5}>
                <Text color="gray">No signals.</Text>
              </Table.Cell>
            </Table.Row>
          )}
        </Table.Body>
      </Table.Root>

      {/* AI Messages */}
      <Heading size="3" mb="2">
        AI Messages ({runMessages.length})
      </Heading>
      {runMessages.length === 0 ? (
        <Text color="gray">No AI messages for this run.</Text>
      ) : (
        <Flex direction="column" gap="2">
          {runMessages.map((msg) => (
            <Card key={msg._id} size="1">
              <Flex justify="between" align="center" mb="1">
                <Text size="1" color="gray">
                  <RelativeTime timestamp={msg.createdAt} />
                </Text>
              </Flex>
              <JsonInspector
                data={msg.message}
                label="Message content"
                defaultOpen
              />
            </Card>
          ))}
        </Flex>
      )}
    </div>
  );
};

const InfoItem: FC<{ label: string; value: string; mono?: boolean }> = ({
  label,
  value,
  mono,
}) => (
  <Flex direction="column" gap="1">
    <Text size="1" color="gray" weight="medium">
      {label}
    </Text>
    <Text size="2" style={mono ? { fontFamily: "monospace" } : undefined}>
      {value}
    </Text>
  </Flex>
);
